import { vec3 } from 'gl-matrix';
import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    environmentState,
    lowerSideViewState,
    mainViewState,
    upperSideViewState,
} from '~/state/atoms/engineAtoms';
import {
    currenPositionAutoScrollState,
    isAutoScrollingState,
} from '~/state/atoms/viewerAtoms';

/**
 * AutoScrolling Hook
 *
 * Method to scroll over all the slices within the bounding box of a given structure.
 * It starts from the first slice of the structure and slowly scrolls to the end slice and back when called.
 *
 * @param structure - The ROI number of the structure to scroll through.
 */
export const useAutoScroll = (structureNumber: number) => {
    const [shouldScroll, setShouldScroll] =
        useRecoilState(isAutoScrollingState);
    const [, setCurrentPosition] = useRecoilState(
        currenPositionAutoScrollState
    );
    const animationFrameRef = useRef<number | null>(null);
    const scrollDirectionRef = useRef<number>(1);
    const positionRef = useRef<vec3 | null>(null);
    const lastTimestampRef = useRef<number | null>(null);
    const environment = useRecoilValue(environmentState);
    const mainView = useRecoilValue(mainViewState);
    const upperSideView = useRecoilValue(upperSideViewState);
    const lowerSideView = useRecoilValue(lowerSideViewState);

    useEffect(() => {
        if (
            !shouldScroll ||
            !environment ||
            !mainView ||
            !upperSideView ||
            !lowerSideView
        ) {
            return;
        }

        const roi = environment.structureSet?.getRoi(structureNumber);
        if (!roi || !roi.texture) {
            return;
        }

        scrollDirectionRef.current = 1;
        lastTimestampRef.current = null;

        const viewDirection = mainView.viewDirection;
        const { min, max, center } = roi.texture.contentBoundingBox;
        const imageGrid = roi._imageGrid;
        const start = vec3.add(
            vec3.create(),
            center,
            vec3.scale(
                vec3.create(),
                viewDirection,
                vec3.dot(
                    viewDirection,
                    vec3.subtract(vec3.create(), min!, center)
                )
            )
        );
        const end = vec3.add(
            vec3.create(),
            center,
            vec3.scale(
                vec3.create(),
                viewDirection,
                vec3.dot(
                    viewDirection,
                    vec3.subtract(vec3.create(), max!, center)
                )
            )
        );

        positionRef.current = vec3.clone(start);
        setCurrentPosition(vec3.clone(start));

        const sliceStep = vec3.dot(viewDirection, imageGrid.getRes());
        // Speed: how many slice-widths per second to traverse.
        // At 140ms per slice this was ~7.1 slices/sec — keep the same pace.
        const speed = sliceStep / 0.14; // mm per second

        const scrollLine = vec3.sub(vec3.create(), end, start);
        const scrollLineDot = vec3.dot(scrollLine, scrollLine);

        const scroll = (timestamp: number) => {
            if (!positionRef.current) {
                return;
            }

            // Compute elapsed time since last frame
            if (lastTimestampRef.current === null) {
                lastTimestampRef.current = timestamp;
            }
            const dt = Math.min(
                (timestamp - lastTimestampRef.current) / 1000,
                0.1 // cap delta to avoid large jumps after tab-switch
            );
            lastTimestampRef.current = timestamp;

            const displacement = speed * dt * scrollDirectionRef.current;

            const newPosition = vec3.add(
                vec3.create(),
                positionRef.current,
                vec3.scale(vec3.create(), viewDirection, displacement)
            );

            const scrollDistance = vec3.dot(
                vec3.sub(vec3.create(), newPosition, start),
                scrollLine
            );

            if (scrollDistance < 0) {
                scrollDirectionRef.current = 1;
                vec3.copy(newPosition, start);
            } else if (scrollDistance > scrollLineDot) {
                scrollDirectionRef.current = -1;
                vec3.copy(newPosition, end);
            }

            positionRef.current = newPosition;
            setCurrentPosition(vec3.clone(newPosition));
            mainView.updatePlaneIntersectionPoint(newPosition);

            animationFrameRef.current = requestAnimationFrame(scroll);
        };

        animationFrameRef.current = requestAnimationFrame(scroll);

        return () => {
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldScroll, structureNumber, environment, mainView]);

    return { shouldScroll, setShouldScroll };
};
